import {
  arrayOf,
  boolOf,
  fail,
  idOf,
  numberOf,
  objectOf,
  optionalText,
  textOf,
} from '../../../shared/api/wire'
import type { AqsiDiscrepancies, AqsiDiscrepancy, AqsiIncident, AqsiIncidentPage, AqsiRecoveryProof } from '../model/types'

export const INCIDENT_KINDS = {
  incidents: 'aqsi.sale-incidents',
  recoveryProof: 'aqsi.sale-recovery-proof',
  discrepancies: 'aqsi.receipt-discrepancies',
} as const

function integerOf(value: unknown, field: string): number {
  const number = numberOf(value, field)
  return Number.isSafeInteger(number) ? number : fail(`${field} — не целое число`)
}

function instantOf(value: unknown, field: string): string {
  const text = textOf(value, field)
  return Number.isNaN(Date.parse(text)) ? fail(`${field} — не дата`) : text
}

function decodeIncident(raw: unknown, field: string): AqsiIncident {
  const item = objectOf(raw, field)
  return {
    saleId: idOf(item.saleId, `${field}.saleId`),
    registerKey: idOf(item.registerKey, `${field}.registerKey`),
    deviceId: integerOf(item.deviceId, `${field}.deviceId`),
    createdAt: instantOf(item.createdAt, `${field}.createdAt`),
    payment: textOf(item.payment, `${field}.payment`),
    fiscal: textOf(item.fiscal, `${field}.fiscal`),
    hasUnresolvedAttempt: boolOf(item.hasUnresolvedAttempt, `${field}.hasUnresolvedAttempt`),
    legacyIsFinished: boolOf(item.legacyIsFinished, `${field}.legacyIsFinished`),
    journalVersion: integerOf(item.journalVersion, `${field}.journalVersion`),
  }
}

/**
 * Страница журнала инцидентов. В журнал места служба включает и продажи его
 * НЫНЕШНЕЙ кассы, начатые на другом месте (кассу перепривязали): строка чужого
 * места здесь законна, а чужой ресурс отвергает конверт (rid ответа).
 */
export function decodeIncidentPage(raw: unknown): AqsiIncidentPage {
  const data = objectOf(raw, 'журнал инцидентов')
  const items = arrayOf(data.items, 'items').map((item, index) => decodeIncident(item, `items[${index}]`))
  const nextCursor = optionalText(data.nextCursor, 'nextCursor')
  const complete = boolOf(data.complete, 'complete')
  if (complete === (nextCursor !== null)) fail('complete и nextCursor противоречат друг другу')
  return {
    items,
    nextCursor,
    snapshotId: idOf(data.snapshotId, 'snapshotId'),
    complete,
    observedAt: instantOf(data.observedAt, 'observedAt'),
  }
}

/** Доказательство: продажа в ответе обязана быть той, что запрошена. */
export function decodeRecoveryProof(raw: unknown, saleId: string): AqsiRecoveryProof {
  const data = objectOf(raw, 'доказательство')
  const answered = idOf(data.saleId, 'saleId')
  if (answered.toLowerCase() !== saleId.toLowerCase()) fail('в ответе другая продажа, чем в запросе')
  return {
    proofId: idOf(data.proofId, 'proofId'),
    saleId: answered,
    originalOperationId: idOf(data.originalOperationId, 'originalOperationId'),
    attemptId: idOf(data.attemptId, 'attemptId'),
    basedOnJournalVersion: integerOf(data.basedOnJournalVersion, 'basedOnJournalVersion'),
    observedAt: instantOf(data.observedAt, 'observedAt'),
    verificationPolicyVersion: textOf(data.verificationPolicyVersion, 'verificationPolicyVersion'),
    evidenceKind: textOf(data.evidenceKind, 'evidenceKind'),
    outcome: textOf(data.outcome, 'outcome'),
    providerOperationId: optionalText(data.providerOperationId, 'providerOperationId'),
    receiptId: optionalText(data.receiptId, 'receiptId'),
    evidenceDigest: textOf(data.evidenceDigest, 'evidenceDigest'),
    nextAction: textOf(data.nextAction, 'nextAction'),
  }
}

function decodeDiscrepancy(raw: unknown, field: string): AqsiDiscrepancy {
  const item = objectOf(raw, field)
  return {
    id: idOf(item.id, `${field}.id`),
    receiptId: optionalText(item.receiptId, `${field}.receiptId`),
    saleId: optionalText(item.saleId, `${field}.saleId`),
    kind: textOf(item.kind, `${field}.kind`),
    detail: textOf(item.detail, `${field}.detail`),
    localValue: optionalText(item.localValue, `${field}.localValue`),
    externalValue: optionalText(item.externalValue, `${field}.externalValue`),
    detectedAt: instantOf(item.detectedAt, `${field}.detectedAt`),
  }
}

export function decodeDiscrepancies(raw: unknown): AqsiDiscrepancies {
  const data = objectOf(raw, 'журнал расхождений')
  return {
    items: arrayOf(data.items, 'items').map((item, index) => decodeDiscrepancy(item, `items[${index}]`)),
    limit: integerOf(data.limit, 'limit'),
  }
}
